import React from 'react';

interface UseIntersectionObserverOptions {
  /**
   * Visibility threshold (0-1) at which the callback is triggered
   * @default 0.5
   */
  threshold?: number;
  /**
   * Margins around the root element
   * @default '0px'
   */
  rootMargin?: string;
  /**
   * Stop observing after the first trigger
   * @default true
   */
  once?: boolean;
  /**
   * Container element for observation (root)
   */
  root?: Element | null;
}

interface UseIntersectionObserverReturn<T extends HTMLElement = HTMLElement> {
  /**
   * Ref for the element to be observed
   */
  ref: React.RefObject<T>;
  /**
   * Flag indicating whether the element is visible
   */
  isIntersecting: boolean;
  /**
   * Intersection ratio (0-1)
   */
  intersectionRatio: number;
}

/**
 * Hook for tracking element visibility using Intersection Observer API
 *
 * @param callback - Function that is called when the element becomes visible
 * @param options - Options for Intersection Observer
 * @returns Object with ref for the element and visibility information
 *
 * @example
 * ```tsx
 * const { ref, isIntersecting } = useIntersectionObserver(() => {
 *   console.log('Element is visible!');
 * });
 *
 * return <div ref={ref}>Content</div>;
 * ```
 */
export const useIntersectionObserver = <T extends HTMLElement = HTMLElement>(
  callback: (entry: IntersectionObserverEntry) => void,
  options: UseIntersectionObserverOptions = {},
): UseIntersectionObserverReturn<T> => {
  const {
    threshold = 0.5,
    rootMargin = '0px',
    once = true,
    root = null,
  } = options;

  const ref = React.useRef<T>(null);
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [intersectionRatio, setIntersectionRatio] = React.useState(0);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isVisible =
          entry.isIntersecting && entry.intersectionRatio >= threshold;

        setIsIntersecting(isVisible);
        setIntersectionRatio(entry.intersectionRatio);

        if (isVisible) {
          callback(entry);

          if (once) {
            observer.disconnect();
          }
        }
      },
      {
        threshold,
        rootMargin,
        root,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [callback, threshold, rootMargin, once, root]);

  return {
    ref,
    isIntersecting,
    intersectionRatio,
  };
};
